import clsx from 'clsx'
import { useFormikContext } from 'formik'
import {
  Input,
  Text,
  Icon,
  Button,
  FormErrorMessage,
  Switch,
  InputLabel,
  ToolTip,
} from 'lib/components'
import React from 'react'
import { FormValues, FormSurveyQuestion } from './types'

interface QuestionResponseProps {
  response: { id: number; responseText: string }
  responseIndex: number
  questionId: number
  onChangeText: (text: string) => void
  onRemove: () => void
}

const QuestionResponse = ({
  response,
  responseIndex,
  questionId,
  onChangeText,
  onRemove,
}: QuestionResponseProps) => {
  const formik = useFormikContext<FormValues>()
  const index = formik.values.questions.findIndex((q) => q.id === questionId)

  return (
    <div className="flex flex-col gap-1">
      <div
        key={response.id}
        className="flex items-center gap-1 rounded:shadow-lg rounded-md"
      >
        <Input
          value={response.responseText}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onChangeText(event.target.value)
          }}
          placeholder="Answer text..."
        />
        <button
          className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center"
          onClick={onRemove}
        >
          <i className="fas fa-trash text-charcoal hover:text-error-40" />
        </button>
      </div>
      <FormErrorMessage
        name={`questions.${index}.responses.${responseIndex}.responseText`}
        showRequiredOnlyOnSumbit
      />
    </div>
  )
}

interface TrustedAppsCustomQuestionRowProps {
  question: FormSurveyQuestion
  isOpen?: boolean
}

export function TrustedAppsCustomQuestionRow({
  question,
  isOpen = false,
}: TrustedAppsCustomQuestionRowProps) {
  const formik = useFormikContext<FormValues>()
  const [open, setOpen] = React.useState(isOpen)
  const index = formik.values.questions.findIndex((q) => q.id === question.id)
  const errors: boolean = formik.errors.questions?.[index] !== undefined

  React.useEffect(() => {
    if (errors) setOpen(true)
  }, []) // eslint-disable-line

  const isMultiple = question.questionType.includes('MULTIPLE_RESPONSES')
  const isExtensible = question.questionType.includes('LIST_UPDATABLE')

  const createQuestionResponse = () => {
    const newResponse = {
      id: Math.random(),
      responseText: '',
      isCreated: true,
    }

    const updatedQuestions = formik.values.questions.map((q) =>
      q.id === question.id
        ? { ...q, responses: [...q.responses, newResponse] }
        : q,
    )

    formik.setFieldValue('questions', updatedQuestions)
  }

  const updateQuestionText = (questionText: string) => {
    const updatedQuestions = formik.values.questions.map((q) =>
      q.id === question.id ? { ...q, questionText } : q,
    )

    formik.setFieldValue('questions', updatedQuestions)
  }

  const changeQuestionExtensibility = () => {
    if (isExtensible) {
      const updatedQuestionType = question.questionType.replace(
        'LIST_UPDATABLE',
        'LIST',
      )

      const updatedQuestions = formik.values.questions.map((q) =>
        q.id === question.id ? { ...q, questionType: updatedQuestionType } : q,
      )

      formik.setFieldValue('questions', updatedQuestions)
    } else {
      const updatedQuestionType = question.questionType.replace(
        'LIST',
        'LIST_UPDATABLE',
      )

      const updatedQuestions = formik.values.questions.map((q) =>
        q.id === question.id ? { ...q, questionType: updatedQuestionType } : q,
      )

      formik.setFieldValue('questions', updatedQuestions)
    }
  }

  const changeQuestionResponseQuantity = () => {
    if (isMultiple) {
      const updatedQuestionType = question.questionType.replace(
        'MULTIPLE_RESPONSES',
        'SINGLE_RESPONSE',
      )

      const updatedQuestions = formik.values.questions.map((q) =>
        q.id === question.id ? { ...q, questionType: updatedQuestionType } : q,
      )

      formik.setFieldValue('questions', updatedQuestions)
    } else {
      const updatedQuestionType = question.questionType.replace(
        'SINGLE_RESPONSE',
        'MULTIPLE_RESPONSES',
      )

      const updatedQuestions = formik.values.questions.map((q) =>
        q.id === question.id ? { ...q, questionType: updatedQuestionType } : q,
      )

      formik.setFieldValue('questions', updatedQuestions)
    }
  }

  const removeQuestion = () => {
    const updatedQuestions = formik.values.questions.filter(
      (q) => q.id !== question.id,
    )

    formik.setFieldValue('questions', updatedQuestions)
  }

  const updateTeacherVisibility = () => {
    const updatedQuestions = formik.values.questions.map((q) =>
      q.id === question.id
        ? { ...q, hideFromTeachers: !question.hideFromTeachers }
        : q,
    )

    formik.setFieldValue('questions', updatedQuestions)
  }

  const updateQuestionResponseText = (
    responseId: number,
    responseText: string,
  ) => {
    const updatedQuestions = formik.values.questions.map((q) => {
      if (q.id === question.id) {
        const updatedResponses = q.responses.map((r) => {
          return r.id === responseId ? { ...r, responseText } : r
        })
        return { ...q, responses: updatedResponses }
      }
      return q
    })

    formik.setFieldValue('questions', updatedQuestions)
  }

  const removeQuestionResponse = (responseId: number) => {
    formik.setFieldValue(
      'questions',
      formik.values.questions.map((q) => {
        return q.id === question.id
          ? {
              ...q,
              responses: q.responses.filter((r) => r.id !== responseId),
            }
          : q
      }),
    )
  }

  return (
    <div className="flex flex-col gap-2 p-4 rounded-md bg-blue-50">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <Input
            value={question.questionText}
            fontSize={16}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              updateQuestionText(event.target.value)
            }}
            placeholder="Question"
          />

          <button
            className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center"
            onClick={updateTeacherVisibility}
            type="button"
          >
            <ToolTip tip="Visible to teachers?">
              <i
                className={clsx('fas text-charcoal hover:text-complete', {
                  'fa-eye': !question.hideFromTeachers,
                  'fa-eye-slash': question.hideFromTeachers,
                })}
              />
            </ToolTip>
          </button>

          <button
            type="button"
            className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center"
            onClick={() => {
              removeQuestion()
            }}
          >
            <i className="fas fa-trash text-charcoal hover:text-error-40" />
          </button>
        </div>
        <FormErrorMessage
          name={`questions.${index}.responses`}
          showRequiredOnlyOnSumbit
        />
        <FormErrorMessage
          name={`questions.${index}.questionText`}
          showRequiredOnlyOnSumbit
        />
      </div>
      <div className="w-full flex flex-col shadow-md rounded-md mt-2 bg-white">
        <div
          className="w-full flex justify-between cursor-pointer py-4 px-4 rounded:shadow-lg rounded-md"
          onClick={() => setOpen(!open)}
        >
          <div className="flex gap-2 items-center">
            <Text fontSize={16}>Answers</Text>
            {errors && (
              <Icon
                className="fas fa-exclamation-circle text-md"
                color="error.40"
              />
            )}
          </div>
          <Icon
            color="textLight"
            className={clsx(
              'fas fa-chevron-up rotate-chevron',
              !open && 'down',
            )}
          />
        </div>
        {open && (
          <div className="flex flex-col items-start px-4 pb-4">
            <div className="flex flex-col gap-2 w-full">
              {question.responses.map((response, responseIndex) => {
                return (
                  <QuestionResponse
                    key={response.id}
                    response={response}
                    responseIndex={responseIndex}
                    questionId={question.id}
                    onChangeText={(text) =>
                      updateQuestionResponseText(response.id, text)
                    }
                    onRemove={() => removeQuestionResponse(response.id)}
                  />
                )
              })}
            </div>
            <Button
              className="mt-4"
              onClick={createQuestionResponse}
              type="button"
            >
              Add Answer
            </Button>
          </div>
        )}
      </div>
      <div className="flex gap-x-4 p-2 my-1">
        <div>
          <InputLabel>Multiple Responses?</InputLabel>
          <Switch
            on={isMultiple}
            onChange={changeQuestionResponseQuantity}
            small
          />
        </div>
        <div>
          <InputLabel>Extensible?</InputLabel>
          <Switch
            on={isExtensible}
            onChange={changeQuestionExtensibility}
            small
          />
        </div>
      </div>
    </div>
  )
}
